import type { AppleSignInResponse } from '@/core/data/auth/apple/apple.d';
import { type AuthParams, type AuthUserDto } from '@/core/data/auth/auth.interface';
import { LoginMethodType } from '@/core/data/auth/auth.type';
import type { CodeErrorResponse, CodeResponse } from '@/core/data/auth/google/oauth2';
import { RouterAuthType, RouterNameType } from '@/core/data/config/uiConfig.interface';
import { gambits, localize } from '@/core/gambits';
import GamHelper from '@/core/helpers/app.helper';
import type { Result } from '@/core/network/http/httpClient.interface';
import { useAuthStore } from '@/stores/auth.store';
import useApple from '@/views/composables/auth/social/useApple';
import useGoogle from '@/views/composables/auth/social/useGoogle';
import useLichess from '@/views/composables/auth/social/useLichess';
import type { AuthModuleData, AuthModuleState, AuthScreenMethod } from '@/views/composables/auth/useAuth.interface';
import { GamButtonMessageVariant } from '@/views/composables/constants/components/gamButton.constants';
import type { GamButtonMessageType } from '@/views/composables/models/components/GamButton';
import { onBeforeMount, reactive, toRefs } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const useAuth = (moduleData: AuthModuleData) => {
  const state = reactive<AuthModuleState>({
    activeMethod: null,
    authMethodGroups: null,
    accessToken: '',
    showAuthMethods: false,
  });

  const { initGoogle, googleSignIn } = useGoogle();
  const { initApple, appleSignIn } = useApple();
  const { lichessSignIn } = useLichess();
  const route = useRoute();
  const router = useRouter();
  const authStore = useAuthStore();

  onBeforeMount(async () => {
    setAuthMethods();
    await checkCodeParams();
  });

  const checkCodeParams = async (): Promise<void> => {
    const authParams: AuthParams = route.query;

    if (authParams?.code) {
      authStore.setConnecting(gambits.authService.getConnecting());
      await getToken(authParams.code);
    }
  };

  const getToken = async (code: string): Promise<void> => {
    if (state.activeMethod) state.activeMethod.isLoading = true;
    const result = await gambits.authService.getToken(code);
    if (state.activeMethod) state.activeMethod.isLoading = false;
    await finalizeSignIn(result);
  };

  const setAuthMethods = (): void => {
    state.authMethodGroups = gambits.authService.getAuthMethods();
    state.authMethodGroups?.forEach((methodGroup) => {
      methodGroup.items?.forEach((signUpMethod: AuthScreenMethod) => {
        if (signUpMethod.type === LoginMethodType.Lichess) {
          state.activeMethod = signUpMethod;
        }
        initSocialMethods(signUpMethod);
      });
    });
    state.showAuthMethods = true;
  };

  const initSocialMethods = (loginMethod: AuthScreenMethod) => {
    switch (loginMethod.type) {
      case LoginMethodType.Google:
        if (loginMethod.clientId) {
          initGoogle((loaded) => {
            loginMethod.active = loaded;
          });
        }
        break;
      case LoginMethodType.Apple:
        if (loginMethod.clientId) {
          const redirectUri = `${GamHelper.getCurrentUrl()}/auth`;
          initApple(loginMethod.clientId, redirectUri, (loaded) => {
            loginMethod.active = loaded;
          });
        }
        break;
      default:
        break;
    }
  };

  const signInWith = async (method: AuthScreenMethod): Promise<void> => {
    state.activeMethod = method;
    switch (method.type) {
      case LoginMethodType.Email: {
        await router.push({ name: RouterAuthType.AUTH_EMAIL });
        break;
      }
      case LoginMethodType.Google: {
        setActiveMethodError(null);
        googleSignIn(method, googleLogin, googleError);
        break;
      }
      case LoginMethodType.Apple: {
        const response = await appleSignIn(method);
        await appleLogin(response);
        break;
      }
      case LoginMethodType.Lichess: {
        await lichessSignIn(moduleData.serverUrl, method);
        break;
      }
      default:
        break;
    }
  };

  const googleLogin = async (response: CodeResponse): Promise<void> => {
    if (response.error) {
      setActiveMethodError({
        label: response.error,
        variant: GamButtonMessageVariant.ERROR,
      });
    } else if (response.code) {
      setActiveMethodError(null);
      authStore.setConnecting(LoginMethodType.Google);
      const result = await gambits.authService.authWithGoogle(response);
      await finalizeSignIn(result);
    }
  };

  const googleError = (error: CodeErrorResponse): void => {
    // popup_closed is a deliberate cancel, so it stays neutral. Anything else
    // means the popup never opened, which a reload clears.
    const isCancelled = error.type === 'popup_closed';

    setActiveMethodError({
      label: localize(isCancelled ? 'auth.social.google.error.cancelled' : 'auth.social.google.error.blocked'),
      variant: isCancelled ? GamButtonMessageVariant.DEFAULT : GamButtonMessageVariant.ERROR,
    });
  };

  const appleLogin = async (response: AppleSignInResponse | null): Promise<void> => {
    if (!response) {
      setActiveMethodError({
        label: 'Apple sign in was cancelled or failed',
        variant: GamButtonMessageVariant.ERROR,
      });
      return;
    }

    setActiveMethodError(null);
    authStore.setConnecting(LoginMethodType.Apple);

    const result = await gambits.authService.authWithApple({
      code: response.authorization.code,
      id_token: response.authorization.id_token,
      user: response.user
        ? {
            email: response.user.email,
            name: response.user.name,
          }
        : undefined,
    });

    await finalizeSignIn(result);
  };

  const finalizeSignIn = async (result: Result<AuthUserDto>): Promise<void> => {
    if (result.data) {
      await authStore.setUser(result.data);
      if (result.data.isActive) {
        await router.push({ name: RouterNameType.SPARKS });
      } else {
        await router.push({ name: RouterAuthType.AUTH_REGISTRATION });
      }
    } else if (result.error) {
      setActiveMethodError({
        label: result.error.message,
        variant: GamButtonMessageVariant.ERROR,
      });
    }
    gambits.authService.removeConnecting();
    authStore.setConnecting(null);
  };

  const setActiveMethodError = (errorMessage: GamButtonMessageType | null): void => {
    if (state.activeMethod) {
      state.activeMethod.errorMessage = errorMessage;
    }
  };

  return {
    ...toRefs(state),
    signInWith,
  };
};

export { useAuth };
